import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
    appInnerCardStyle,
    appMutedTextStyle,
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import { STEP02_SPECIAL_INSTRUCTION_OPTIONS } from "../nurseAssessmentFormConstants";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Step02FormValues {
    // Primary evacuation status — mutually exclusive
    isEvacuationIndependent: boolean;
    requiresEvacuationAssistance: boolean;

    // Special instructions (relevant when assistance is required)
    requiresTotalOneToOneAssistance: boolean;
    requiresAssistiveDevice: boolean;
    assistiveDeviceType: string;
    requiresWheelchair: boolean;
    bedroomMustBeOnGroundLevel: boolean;
    hasOtherEvacuationRequirements: boolean;
    otherEvacuationRequirementsDetail: string;

    // Free-text notes
    evacuationAdditionalNotes: string;
}

export const DEFAULT_STEP02: Step02FormValues = {
    isEvacuationIndependent: false,
    requiresEvacuationAssistance: false,
    requiresTotalOneToOneAssistance: false,
    requiresAssistiveDevice: false,
    assistiveDeviceType: "",
    requiresWheelchair: false,
    bedroomMustBeOnGroundLevel: false,
    hasOtherEvacuationRequirements: false,
    otherEvacuationRequirementsDetail: "",
    evacuationAdditionalNotes: "",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

const LabeledField: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
    <div className="flex flex-column gap-1 mb-3">
        <label className="font-medium text-sm" style={{ color: "#374151" }}>
            {label}{required && <span style={{ color: "#dc2626", marginLeft: 2 }}>*</span>}
        </label>
        {children}
    </div>
);

// ── Props ─────────────────────────────────────────────────────────────────────

export interface Step02Props {
    sectionData: Partial<Step02FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step02FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

const Step02EmergencyEvacuation: React.FC<Step02Props> = ({
    sectionData, residentId, formId, onSave, onNext, onBack, loading,
}) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);

    const defaultValues: Step02FormValues = {
        ...DEFAULT_STEP02,
        ...(sectionData || {}),
    };

    const { control, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<Step02FormValues>({ defaultValues });

    const isIndependent = watch("isEvacuationIndependent");
    const requiresAssistance = watch("requiresEvacuationAssistance");
    const requiresAssistiveDevice = watch("requiresAssistiveDevice");
    const hasOtherRequirements = watch("hasOtherEvacuationRequirements");

    // Mutually exclusive toggle for the two evacuation status options
    const handleIndependentChange = (checked: boolean) => {
        setValue("isEvacuationIndependent", checked);
        if (checked) setValue("requiresEvacuationAssistance", false);
    };

    const handleAssistanceChange = (checked: boolean) => {
        setValue("requiresEvacuationAssistance", checked);
        if (checked) setValue("isEvacuationIndependent", false);
    };

    // Silent submit used only by auto-save
    const onSubmit = async (data: Step02FormValues) => {
        await onSave(data);
    };

    // Manual save — shows success / failure toast
    const handleSave = async (data: Step02FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Emergency evacuation section saved successfully.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message || "An error occurred while saving.", life: 4000 });
        }
    };

    // Save & continue — only advances if save succeeds
    const handleSaveAndContinue = async (data: Step02FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Emergency evacuation saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message || "An error occurred while saving.", life: 4000 });
        }
    };

    useNurseFormAutoSave({
        enabled: enableEditing && !!formId,
        baselineData: defaultValues,
        watch,
        handleSubmit,
        onSubmit,
    });

    const values = watch();

    return (
        <>
            <Toast ref={toast} />

            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Emergency Evacuation", "Evacuation status and special instructions.", "pi pi-exclamation-triangle")}
                            <div className="p-3 grid">
                                <InfoRow label="Evacuation Status" value={values.isEvacuationIndependent ? "Independent" : values.requiresEvacuationAssistance ? "Assistance Required" : undefined} />
                                <InfoRow label="Total 1:1 Assistance" value={values.requiresTotalOneToOneAssistance ? "Yes" : "No"} />
                                <InfoRow label="Wheelchair Required" value={values.requiresWheelchair ? "Yes" : "No"} />
                                <InfoRow label="Bedroom on Ground Level" value={values.bedroomMustBeOnGroundLevel ? "Yes" : "No"} />
                                <InfoRow label="Assistive Device" value={values.requiresAssistiveDevice ? (values.assistiveDeviceType || "Yes") : "No"} />
                                <InfoRow label="Other Requirements" value={values.hasOtherEvacuationRequirements ? (values.otherEvacuationRequirementsDetail || "Yes") : "No"} />
                                <InfoRow label="Additional Notes" value={values.evacuationAdditionalNotes} />
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        {/* ── Evacuation Status ────────────────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader(
                                "Emergency Evacuation",
                                "Indicate the individual's ability to self-evacuate in an emergency.",
                                "pi pi-exclamation-triangle",
                            )}
                            <div className="p-3">

                                {/* INDEPENDENT */}
                                <div
                                    style={{
                                        ...appInnerCardStyle,
                                        border: isIndependent ? "1.5px solid #16a34a" : undefined,
                                        background: isIndependent ? "#f0fdf4" : undefined,
                                    }}
                                    className="mb-3 p-3"
                                >
                                    <Controller
                                        name="isEvacuationIndependent"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="flex align-items-start gap-3">
                                                <Checkbox
                                                    inputId="evacuation-independent"
                                                    checked={field.value}
                                                    onChange={(e) => handleIndependentChange(!!e.checked)}
                                                    style={{ marginTop: 2, flexShrink: 0 }}
                                                />
                                                <label htmlFor="evacuation-independent" className="cursor-pointer">
                                                    <span className="font-bold" style={{ color: "#15803d" }}>INDEPENDENT</span>
                                                    <span style={{ color: "#374151" }}>
                                                        {" — Physically and mentally capable of safely getting out of the home, including the ascent and descent of stairs, without the assistance of another individual or the use of mobility aids."}
                                                    </span>
                                                    <span style={{ ...appMutedTextStyle, display: "block", marginTop: 4, fontSize: "0.82rem" }}>
                                                        The client is considered independent if capable of getting out after one verbal cue.
                                                    </span>
                                                </label>
                                            </div>
                                        )}
                                    />
                                </div>

                                {/* ASSISTANCE REQUIRED */}
                                <div
                                    style={{
                                        ...appInnerCardStyle,
                                        border: requiresAssistance ? "1.5px solid #dc2626" : undefined,
                                        background: requiresAssistance ? "#fef2f2" : undefined,
                                    }}
                                    className="mb-3 p-3"
                                >
                                    <Controller
                                        name="requiresEvacuationAssistance"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="flex align-items-start gap-3">
                                                <Checkbox
                                                    inputId="evacuation-assistance"
                                                    checked={field.value}
                                                    onChange={(e) => handleAssistanceChange(!!e.checked)}
                                                    style={{ marginTop: 2, flexShrink: 0 }}
                                                />
                                                <label htmlFor="evacuation-assistance" className="cursor-pointer">
                                                    <span className="font-bold" style={{ color: "#b91c1c" }}>ASSISTANCE REQUIRED</span>
                                                    <span style={{ color: "#374151" }}>
                                                        {" — Physically or mentally unable to walk or traverse a normal path to safety without the physical assistance of another person or the use of mobility aids."}
                                                    </span>
                                                </label>
                                            </div>
                                        )}
                                    />
                                </div>

                                {errors.isEvacuationIndependent && (
                                    <small className="p-error">Select an evacuation status.</small>
                                )}
                            </div>
                        </Card>

                        {/* ── Special Instructions ─────────────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Special Instructions", undefined, "pi pi-list")}
                            <div className="p-3">
                                <div style={appMutedTextStyle} className="mb-3">
                                    Specify any special requirements for safe evacuation. Complete this section whenever assistance is required or additional accommodations are needed.
                                </div>

                                <div className="flex flex-column gap-3">
                                    {/* Simple checkbox options from constants */}
                                    {STEP02_SPECIAL_INSTRUCTION_OPTIONS.map(([fname, lbl]) => (
                                        <Controller
                                            key={fname}
                                            name={fname}
                                            control={control}
                                            render={({ field }) => (
                                                <div className="flex align-items-center gap-2">
                                                    <Checkbox
                                                        inputId={`si-${fname}`}
                                                        checked={field.value}
                                                        onChange={(e) => field.onChange(e.checked)}
                                                    />
                                                    <label htmlFor={`si-${fname}`} className="cursor-pointer">{lbl}</label>
                                                </div>
                                            )}
                                        />
                                    ))}

                                    {/* Requires assistive device + type text */}
                                    <div>
                                        <Controller
                                            name="requiresAssistiveDevice"
                                            control={control}
                                            render={({ field }) => (
                                                <div className="flex align-items-center gap-2 mb-2">
                                                    <Checkbox
                                                        inputId="si-requiresAssistiveDevice"
                                                        checked={field.value}
                                                        onChange={(e) => field.onChange(e.checked)}
                                                    />
                                                    <label htmlFor="si-requiresAssistiveDevice" className="cursor-pointer">Requires assistive device</label>
                                                </div>
                                            )}
                                        />
                                        {requiresAssistiveDevice && (
                                            <div className="ml-5">
                                                <LabeledField label="Device Type">
                                                    <Controller
                                                        name="assistiveDeviceType"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <InputText
                                                                {...field}
                                                                className="w-full"
                                                                placeholder="e.g. walker, cane, gait belt"
                                                                style={{ maxWidth: 380 }}
                                                            />
                                                        )}
                                                    />
                                                </LabeledField>
                                            </div>
                                        )}
                                    </div>

                                    {/* Other + detail text */}
                                    <div>
                                        <Controller
                                            name="hasOtherEvacuationRequirements"
                                            control={control}
                                            render={({ field }) => (
                                                <div className="flex align-items-center gap-2 mb-2">
                                                    <Checkbox
                                                        inputId="si-hasOtherEvacuationRequirements"
                                                        checked={field.value}
                                                        onChange={(e) => field.onChange(e.checked)}
                                                    />
                                                    <label htmlFor="si-hasOtherEvacuationRequirements" className="cursor-pointer">Other</label>
                                                </div>
                                            )}
                                        />
                                        {hasOtherRequirements && (
                                            <div className="ml-5">
                                                <LabeledField label="Describe other requirement">
                                                    <Controller
                                                        name="otherEvacuationRequirementsDetail"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <InputText
                                                                {...field}
                                                                className="w-full"
                                                                placeholder="Describe the requirement"
                                                                style={{ maxWidth: 480 }}
                                                            />
                                                        )}
                                                    />
                                                </LabeledField>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Additional notes */}
                                <div className="mt-4">
                                    <LabeledField label="Additional Notes / Special Instructions">
                                        <Controller
                                            name="evacuationAdditionalNotes"
                                            control={control}
                                            render={({ field }) => (
                                                <InputTextarea
                                                    {...field}
                                                    className="w-full"
                                                    rows={3}
                                                    autoResize
                                                    placeholder="Any other relevant information about this individual's evacuation needs…"
                                                />
                                            )}
                                        />
                                    </LabeledField>
                                </div>
                            </div>
                        </Card>

                    </>)}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step02EmergencyEvacuation;
